import { useEffect, useRef, useState } from "react";
import { LuArrowLeft, LuHistory, LuMessageSquarePlus } from "react-icons/lu";
import ApiClass from "../api";
import persistedState from "../persited_store";
import utilityfun from "../helpers/utilityfun";
import { WritingReply } from "./WritingReply";
import { parseStructuredReply } from "./ChatStructuredReply";
import {
  applyImageResponse,
  beginImageJobs,
  cancelImageJobs,
  ensureQuestionIds,
  finishImageJob,
  imageJobStatus,
} from "../helpers/imageJobs";
import "../assets/css/askLoading.css";

const compactQuestionForApi = (question) => {
  if (!question || typeof question !== "object") return question;
  const imageUrl = String(question.imageUrl || "");
  const next = { ...question };
  if (imageUrl.startsWith("data:")) delete next.imageUrl;
  if (String(next.imagePrompt || "").length > 500) {
    next.imagePrompt = String(next.imagePrompt).slice(0, 500);
  }
  delete next.altText;
  delete next.generatingVisual;
  return next;
};

const referenceIdsFromReply = (parsed) => {
  if (!parsed || typeof parsed !== "object") return null;
  const topics = [];
  const addTopic = (topic, strand) => {
    const topicName = String(topic?.topicName || topic?.name || "").trim();
    const topicId = Number(topic?.topicId || topic?.id) || 0;
    if (!topicName && !topicId) return;
    topics.push({
      topicId,
      topicName,
      strandId: Number(topic?.strandId || strand?.strandId || strand?.id) || 0,
      strand: String(topic?.strand || strand?.strand || strand?.name || "").trim(),
    });
  };
  (Array.isArray(parsed.strands) ? parsed.strands : []).forEach((strand) => {
    (Array.isArray(strand?.topics) ? strand.topics : []).forEach((topic) => addTopic(topic, strand));
  });
  (Array.isArray(parsed.topics) ? parsed.topics : []).forEach((topic) => addTopic(topic, parsed));
  const refs = {
    gradeId: Number(parsed.gradeId) || 0,
    grade: String(parsed.grade || ""),
    subjectGradeId: Number(parsed.subjectGradeId) || 0,
    subject: String(parsed.subject || ""),
    strandId: Number(parsed.strandId) || 0,
    strand: String(parsed.strand || ""),
    topicId: Number(parsed.topicId) || 0,
    topicName: String(parsed.topicName || ""),
    topics,
  };
  const hasIds =
    refs.gradeId ||
    refs.subjectGradeId ||
    refs.strandId ||
    refs.topicId ||
    refs.subject ||
    refs.topics.length;
  return hasIds ? refs : null;
};

const compactAskHistory = (rows) =>
  rows
    .filter((item) => item.from === "user" || item.from === "bot")
    .slice(-20)
    .map((item) => {
      const role = item.from === "bot" ? "assistant" : "user";
      const raw = String(item.text || "");
      if (role === "user") return { role, content: raw.slice(0, 4000) };
      const parsed = parseStructuredReply(raw);
      if (!parsed || typeof parsed !== "object") {
        return { role, content: raw.slice(0, 4000) };
      }
      const compact = { ...parsed };
      if (Array.isArray(compact.questions)) {
        compact.questions = compact.questions.map(compactQuestionForApi);
      }
      if (Array.isArray(compact.panels)) {
        compact.panels = compact.panels.map((panel) => ({
          id: panel?.id,
          description: String(panel?.description || "").slice(0, 600),
        }));
      }
      if (String(compact.imageUrl || "").startsWith("data:")) delete compact.imageUrl;
      if (String(compact.storyImageUrl || "").startsWith("data:")) delete compact.storyImageUrl;
      delete compact.visualPrompt;
      const refs = referenceIdsFromReply(compact);
      return {
        role,
        content: JSON.stringify(compact).slice(0, 24000),
        ...(refs ? { refs } : {}),
      };
    });

const AGENTS = [
  { slug: "standard", name: "Standard Agent" },
  { slug: "question", name: "Question Agent" },
  { slug: "image", name: "Image Generate Agent" },
];

function loggedInName() {
  const user = persistedState.user();
  if (!user) return "";
  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.fullName ||
    user.userName ||
    ""
  );
}

const isAdminUser = () =>
  utilityfun.isAdmin() || utilityfun.isDistrictAdmin() || utilityfun.isSchoolAdmin();

const GeneratingStatus = () => (
  <div className="ask-loading-bubble" aria-label="Writing">
    <div className="ask-loading-dots">
      <span />
      <span />
      <span />
    </div>
  </div>
);

export function ChatBot({ bottom = 16 }) {
  const showAskAi = isAdminUser();
  const [open, setOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("default");
  const [input, setInput] = useState("");
  const [askInput, setAskInput] = useState("");
  const [chatId, setChatId] = useState(null);
  const [askChatId, setAskChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [askLoading, setAskLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [askMessages, setAskMessages] = useState([]);
  const [agentSlug, setAgentSlug] = useState("question");
  const [askHistoryOpen, setAskHistoryOpen] = useState(false);
  const [askThreads, setAskThreads] = useState([]);
  const [askThreadsLoading, setAskThreadsLoading] = useState(false);
  const [askThreadsMore, setAskThreadsMore] = useState(false);
  const askHistoryRef = useRef(null);
  const askThreadsCursor = useRef("");
  const endRef = useRef(null);
  const askEndRef = useRef(null);
  const askListRef = useRef(null);
  const askStickToBottom = useRef(true);
  const chatListRef = useRef(null);
  const chatStickToBottom = useRef(true);
  const inputRef = useRef(null);
  const askInputRef = useRef(null);
  const startingRef = useRef(false);

  const focusInput = (ref) => {
    requestAnimationFrame(() => ref.current?.focus());
  };

  const startChat = async () => {
    if (startingRef.current || chatId) return;
    startingRef.current = true;
    setLoading(true);
    try {
      const body = {};
      const name = loggedInName();
      if (name) body.name = name;
      const res = await ApiClass.postNodeRequest(
        "chatbot/start",
        !!persistedState.token(),
        body
      );
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        if (data?.chatId) setChatId(data.chatId);
        if (data?.reply) {
          setMessages((m) => [...m, { from: "bot", text: data.reply }]);
        }
      } else {
        startingRef.current = false;
        setMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      startingRef.current = false;
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setLoading(false);
      focusInput(inputRef);
    }
  };

  useEffect(() => {
    if (!open) return;
    focusInput(inputRef);
    startChat();
  }, [open]);

  useEffect(() => {
    if (!askOpen) return;
    if (!askMessages.length) {
      const agent = AGENTS.find((item) => item.slug === agentSlug);
      setAskMessages([
        {
          from: "bot",
          text: `Ask AI is using ${agent?.name || "the selected agent"} with its configured prompt, skills, and knowledge.`,
          instant: true,
          notice: true,
        },
      ]);
    }
    focusInput(askInputRef);
  }, [askOpen]);

  useEffect(() => {
    if (!chatStickToBottom.current) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const list = askListRef.current;
    if (list?.dataset.visualHold === "1") {
      delete list.dataset.visualHold;
      return;
    }
    if (!askStickToBottom.current) return;
    if (list) {
      list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
      return;
    }
    askEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [askMessages, askOpen]);

  const onListScroll = (event, stickRef) => {
    const el = event.currentTarget;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
  };

  useEffect(() => {
    // Hide only Zendesk's own launcher; the Help button opens the widget instead.
    // Do not call $zopim.livechat.button.hide(): for this key it hides the whole widget,
    // so window.show() from Help can no longer open it.
    const style = document.createElement("style");
    style.textContent =
      'iframe#launcher, iframe[title="Button to launch messaging window"] { display: none !important; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    chatStickToBottom.current = true;

    setInput("");
    setMessages((m) => [...m, { from: "user", text }]);
    setLoading(true);
    focusInput(inputRef);

    try {
      const body = { message: text, chatId };
      if (!chatId) {
        const name = loggedInName();
        if (name) body.name = name;
      }
      const res = await ApiClass.postNodeRequest(
        "chatbot/ask",
        !!persistedState.token(),
        body
      );
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        if (data?.chatId) setChatId(data.chatId);
        setMessages((m) => [...m, { from: "bot", text: data?.reply || "" }]);
      } else {
        setMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setLoading(false);
      focusInput(inputRef);
    }
  };

  const sendAskAiMessage = async (text) => {
    if (!text || askLoading) return;

    const history = compactAskHistory(askMessages);
    const botIndex = askMessages.length + 1;
    askStickToBottom.current = true;

    setAskInput("");
    setAskMessages((m) => [...m, { from: "user", text }]);
    setAskLoading(true);
    focusInput(askInputRef);

    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai", true, {
        message: text,
        agentSlug,
        history,
        chatId: askChatId,
      });
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        const chatId = data?.chatId || askChatId;
        if (data?.chatId) setAskChatId(data.chatId);
        const replyText = data?.reply || "";
        setAskMessages((m) => [
          ...m,
          {
            from: "bot",
            text: replyText,
            messageId: data?.messageId || null,
            feedback: null,
          },
        ]);
        const parsed = parseStructuredReply(replyText);
        const targets = Array.isArray(parsed?.visualIndexes)
          ? parsed.visualIndexes
              .map((value) => Number(value) - 1)
              .filter((index) => Number.isInteger(index) && index >= 0)
          : [];
        if (parsed?.generatePanels && Array.isArray(parsed?.panels) && parsed.panels.length) {
          setAskLoading(false);
          const storyImage = await addAskPanels(botIndex, parsed, chatId);
          const extras = (parsed.questions || [])
            .map((question, index) => (question?.needsImage === true && !question?.imageUrl && !question?.questionImgName ? index : -1))
            .filter((index) => index >= 0);
          if (extras.length) {
            await addAskVisuals(botIndex, { ...parsed, ...(storyImage || {}) }, extras, { ignoreLoading: true, chatId });
          }
          return;
        }
        if (parsed?.autoAddVisuals && Array.isArray(parsed?.questions) && targets.length) {
          setAskLoading(false);
          await addAskVisuals(botIndex, parsed, targets, { ignoreLoading: true, chatId });
          return;
        }
      } else {
        setAskMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      setAskMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setAskLoading(false);
      focusInput(askInputRef);
    }
  };

  const patchAskMessage = (index, updater) => {
    setAskMessages((rows) =>
      rows.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const parsed = parseStructuredReply(row.text);
        if (!parsed || typeof parsed !== "object") return row;
        return { ...row, text: JSON.stringify(updater(parsed)), instant: true };
      })
    );
  };

  const saveAskStandards = async (index, data) => {
    if (askLoading) return;
    patchAskMessage(index, (parsed) => ({ ...parsed, saving: true }));
    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai/save-standards", true, data);
      const { status_code, data: payload, message } = res?.data || {};
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: false,
        saving: false,
        savedStandards:
          payload?.savedStandards || {
            ok: status_code == 1,
            message: message || "Unable to save standards.",
          },
      }));
    } catch {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        saving: false,
        savedStandards: { ok: false, message: "Unable to save standards." },
      }));
    }
  };

  const cancelAskStandards = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      pendingSave: false,
      saving: false,
      discarded: true,
    }));
  };

  const saveAskQuestions = async (index, data) => {
    if (data?.saving) return;
    if (Array.isArray(data?.savedQuestions?.questionIds) && data.savedQuestions.questionIds.length) {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: false,
        saving: false,
        savedQuestions: parsed.savedQuestions,
      }));
      return;
    }
    patchAskMessage(index, (parsed) => ({ ...parsed, saving: true }));
    try {
      const storyImageUrl = String(data?.storyImageUrl || "");
      const res = await ApiClass.postNodeRequest("admin/ask-ai/save-questions", true, {
        ...data,
        storyImageUrl: storyImageUrl.startsWith("data:") && data?.storyImageName ? "" : storyImageUrl,
        questions: (data.questions || []).map(compactQuestionForApi),
        saveFingerprint: data.saveFingerprint || undefined,
      });
      const { status_code, data: payload, message } = res?.data || {};
      const saved =
        payload?.savedQuestions || {
          ok: status_code == 1,
          message: message || "Unable to save questions.",
        };
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: saved.ok ? false : true,
        saving: false,
        savedQuestions: saved,
      }));
    } catch {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        saving: false,
        savedQuestions: { ok: false, message: "Unable to save questions." },
      }));
    }
  };

  const cancelAskQuestions = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      pendingSave: false,
      pendingVisuals: false,
      saving: false,
      discarded: true,
    }));
  };

  const setAskFeedback = async (index, nextFeedback) => {
    const row = askMessages[index];
    if (!row || row.from !== "bot" || !row.messageId) return;
    if (row.feedbackNoted || row.feedback) return;
    const feedback = nextFeedback === "like" || nextFeedback === "unlike" ? nextFeedback : null;
    if (!feedback) return;
    setAskMessages((rows) =>
      rows.map((item, itemIndex) =>
        itemIndex === index ? { ...item, feedback, feedbackNoted: true } : item
      )
    );
    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai/feedback", true, {
        messageId: row.messageId,
        feedback,
      });
      const { status_code, data, message } = res?.data || {};
      if (status_code != 1) {
        setAskMessages((rows) =>
          rows.map((item, itemIndex) =>
            itemIndex === index ? { ...item, feedback: null, feedbackNoted: false } : item
          )
        );
        console.log(message || "Unable to save feedback.");
      } else if (data?.feedback) {
        setAskMessages((rows) =>
          rows.map((item, itemIndex) =>
            itemIndex === index
              ? { ...item, feedback: data.feedback, feedbackNoted: true }
              : item
          )
        );
      }
    } catch {
      setAskMessages((rows) =>
        rows.map((item, itemIndex) =>
          itemIndex === index ? { ...item, feedback: null, feedbackNoted: false } : item
        )
      );
    }
  };

  const skipAskVisuals = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      questions: cancelImageJobs(parsed.questions || []),
      pendingVisuals: false,
      addingVisuals: false,
      visualsSkipped: true,
    }));
  };

  const addAskPanels = async (index, data, chatId = askChatId) => {
    const panels = (data.panels || []).map((panel) => ({
      id: panel?.id,
      description: String(panel?.description || "").slice(0, 600),
    }));
    const show = (extra = {}) => {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        ...extra,
        panels,
        questions: (parsed.questions || data.questions || []).map((question) => {
          if (question?.needsImage === true) return question;
          return {
            ...question,
            needsImage: false,
            imageUrl: "",
            questionImgName: null,
            visualStatus: "Not needed",
          };
        }),
      }));
    };
    show({ visualProgress: `Drawing the ${panels.length}-panel story…`, storyImageError: "" });
    try {
      const res = await Promise.race([
        ApiClass.postNodeRequest("admin/ask-ai", true, {
          message: `Draw this ${panels.length}-panel picture story`,
          agentSlug: "question",
          chatId,
          addVisuals: true,
          storyStrip: true,
          panels,
          questions: (data.questions || []).map((question) => {
            const next = compactQuestionForApi(question);
            delete next.imageUrl;
            delete next.questionImgName;
            return next;
          }),
          topicId: data.topicId,
          topicName: data.topicName,
          grade: data.grade,
          subject: data.subject,
          strand: data.strand,
          sourceContent: data.sourceContent || "",
          sourceTitle: data.sourceTitle || "",
          sourceContentType: data.sourceContentType || "",
          standard: data.standard || "",
          history: [],
        }),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error("timeout")), 90000);
        }),
      ]);
      const parsedReply = res?.data?.status_code == 1 ? parseStructuredReply(res.data.data?.reply) : null;
      const storyImage = {
        storyImageUrl: parsedReply?.storyImageUrl || "",
        storyImageName: parsedReply?.storyImageName || "",
        storyImageError: parsedReply?.storyImageUrl ? "" : parsedReply?.storyImageError || "Unable to draw this story.",
      };
      show({
        visualProgress: "",
        ...storyImage,
      });
      return storyImage;
    } catch (error) {
      show({
        visualProgress: "",
        storyImageError: error?.message === "timeout" ? "Image generation timed out." : "Unable to draw this story.",
      });
    }
  };

  const addAskVisuals = async (index, data, preferredIndexes, options = {}) => {
    if ((!options.ignoreLoading && askLoading) || data.addingVisuals) return;
    const requested = Array.isArray(preferredIndexes) ? preferredIndexes : [];
    const defaults = (data.questions || [])
      .map((question, questionIndex) =>
        question.needsImage && !question.imageUrl && !question.questionImgName ? questionIndex : -1
      )
      .filter((questionIndex) => questionIndex >= 0);
    const targets = (requested.length ? requested : defaults)
      .filter((questionIndex) => questionIndex >= 0 && questionIndex < (data.questions || []).length)
      .slice(0, 12);
    if (!targets.length) {
      patchAskMessage(index, (parsed) => ({ ...parsed, pendingVisuals: false, addingVisuals: false }));
      return;
    }

    const history = compactAskHistory(askMessages);
    const chatId = options.chatId || askChatId;
    const identified = ensureQuestionIds(data.questions || []);
    const targetIds = targets
      .map((questionIndex) => identified[questionIndex]?.id)
      .filter((id) => imageJobStatus(identified.find((question) => question.id === id)) !== "success");
    if (!targetIds.length) {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        questions: identified,
        pendingVisuals: false,
        addingVisuals: false,
      }));
      return;
    }
    let working = identified;
    const showJobs = (extra = {}) => {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        ...extra,
        questions: working,
        addingVisuals: working.some((question) => question.generatingVisual),
        pendingVisuals: working.some(
          (question) => question.needsImage && !question.imageUrl && !question.questionImgName
        ),
      }));
    };
    showJobs({ visualError: "", visualProgress: "" });
    const failJobs = (ids, error) => {
      ids.forEach((id) => {
        working = finishImageJob(working, id, { ok: false, error });
      });
    };
    const requestImages = async (ids) => {
      const indexes = ids
        .map((id) => working.findIndex((question) => question.id === id))
        .filter((questionIndex) => questionIndex >= 0);
      try {
        const res = await Promise.race([
          ApiClass.postNodeRequest("admin/ask-ai", true, {
            message: ids.length > 1 ? "Add images to all questions" : "Add visuals to the required questions",
            agentSlug: "question",
            chatId,
            addVisuals: true,
            visualIndexes: indexes,
            visualIndex: indexes.length === 1 ? indexes[0] : undefined,
            questions: working.map(compactQuestionForApi),
            topicId: data.topicId,
            topicName: data.topicName,
            grade: data.grade,
            subject: data.subject,
            strand: data.strand,
            sourceContent: data.sourceContent || "",
            sourceTitle: data.sourceTitle || "",
            sourceContentType: data.sourceContentType || "",
            standard: data.standard || "",
            panels: data.panels || [],
            storyImageName: data.storyImageName || "",
            storyImageUrl: String(data.storyImageUrl || "").startsWith("data:") ? "" : data.storyImageUrl || "",
            history,
          }),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error("timeout")), 90000);
          }),
        ]);
        const { status_code, data: payload, message } = res?.data || {};
        if (payload?.chatId) setAskChatId(payload.chatId);
        const parsedReply = status_code == 1 ? parseStructuredReply(payload?.reply) : null;
        const returned = (parsedReply?.questions || []).map((question, itemIndex) => ({
          ...question,
          id: question?.id || working[itemIndex]?.id,
        }));
        if (returned.length) working = applyImageResponse(working, returned);
        else failJobs(ids, message || "Unable to add this visual.");
      } catch (error) {
        failJobs(ids, error?.message === "timeout" ? "Image generation timed out." : "Unable to add this visual.");
      }
    };
    const historyText = (history || []).map((item) => String(item?.content || item?.text || "")).join("\n");
    const stems = targetIds
      .map((id) => working.find((question) => question.id === id)?.stem || "")
      .join(" ");
    const chartLabels = [...historyText.matchAll(/\b([A-Za-z]{3,})\s+\d+\s*%/g)].map((match) => match[1].toLowerCase());
    const sharedChart =
      /\d+\s*%/.test(historyText) &&
      /\b(chart|pie)\b/i.test(historyText) &&
      (chartLabels.filter((label) => stems.toLowerCase().includes(label)).length >= 2 || /\bpie chart\b/i.test(stems));
    const sharedPicture =
      targetIds.length > 1 &&
      targetIds.every((id) =>
        [...String(working.find((question) => question.id === id)?.imagePrompt || "").matchAll(/\b[A-Za-z]{3,}\s+\d+\b/g)].length >= 2
      );
    if (targetIds.length > 1 && (sharedChart || sharedPicture)) {
      working = beginImageJobs(working, targetIds);
      showJobs({ visualProgress: sharedPicture && !sharedChart ? "Generating the shared picture…" : "Generating the shared chart…" });
      await requestImages(targetIds);
    } else for (let place = 0; place < targetIds.length; place += 1) {
      const id = targetIds[place];
      if (imageJobStatus(working.find((question) => question.id === id)) === "success") continue;
      working = beginImageJobs(working, [id]);
      showJobs({ visualProgress: `Generating visual ${place + 1} of ${targetIds.length}…` });
      await requestImages([id]);
      showJobs();
    }
    showJobs({ visualProgress: "", visualError: "" });
    focusInput(askInputRef);
  };


  const sendAskAi = (e) => {
    e.preventDefault();
    sendAskAiMessage(askInput.trim());
  };

  const openHelp = () => {
    try {
      if (window.$zopim?.livechat?.window?.show) {
        window.$zopim.livechat.window.show();
        return;
      }
    } catch {
      /* ignore */
    }
    try {
      window.zE?.("webWidget", "open");
    } catch {
      /* ignore */
    }
    try {
      window.zE?.("messenger", "open");
    } catch {
      /* ignore */
    }
  };

  const isAgentNotice = (text) => {
    const value = String(text || "").trim();
    return AGENTS.some(
      (agent) => value === introForAgent(agent.slug, false) || value === introForAgent(agent.slug, true)
    );
  };

  const introForAgent = (slug, switched) => {
    const agent = AGENTS.find((item) => item.slug === slug);
    const name = agent?.name || "the selected agent";
    return switched
      ? `Switched to ${name}. Ask using this agent's prompt, skills, and knowledge.`
      : `Ask AI is using ${name} with its configured prompt, skills, and knowledge.`;
  };

  const startNewAskThread = () => {
    setAskHistoryOpen(false);
    setAskChatId(null);
    setAskMessages([
      {
        from: "bot",
        text: introForAgent(agentSlug, false),
        instant: true,
        notice: true,
      },
    ]);
    focusInput(askInputRef);
  };

  const loadAskThreads = async ({ before = "", replace = false } = {}) => {
    if (replace) setAskThreadsLoading(true);
    else setAskThreadsMore(true);
    try {
      const params = new URLSearchParams({ agentSlug });
      if (before) params.set("before", before);
      const res = await ApiClass.getNodeRequest(`admin/ask-ai/threads?${params.toString()}`);
      const body = res?.data?.status_code == 1 ? res.data.data : null;
      const rows = Array.isArray(body) ? body : Array.isArray(body?.threads) ? body.threads : [];
      askThreadsCursor.current = body && !Array.isArray(body) ? body.nextCursor || "" : "";
      setAskThreads((current) => {
        if (replace) return rows;
        const seen = new Set(current.map((row) => row.chatId));
        return [...current, ...rows.filter((row) => row?.chatId && !seen.has(row.chatId))];
      });
    } catch {
      if (replace) setAskThreads([]);
      askThreadsCursor.current = "";
    } finally {
      if (replace) setAskThreadsLoading(false);
      else setAskThreadsMore(false);
    }
  };

  const openAskHistory = () => {
    setAskHistoryOpen(true);
    askThreadsCursor.current = "";
    loadAskThreads({ replace: true });
  };

  const onHistoryScroll = (event) => {
    const node = event.currentTarget;
    if (!askThreadsCursor.current || askThreadsLoading || askThreadsMore) return;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 48) {
      loadAskThreads({ before: askThreadsCursor.current });
    }
  };

  useEffect(() => {
    if (!askHistoryOpen || askThreadsLoading || askThreadsMore) return;
    const node = askHistoryRef.current;
    if (!node || !askThreadsCursor.current) return;
    if (node.scrollHeight <= node.clientHeight + 8) {
      loadAskThreads({ before: askThreadsCursor.current });
    }
  }, [askHistoryOpen, askThreads, askThreadsLoading, askThreadsMore]);

  const openAskThread = async (chatId) => {
    if (!chatId || askThreadsLoading) return;
    setAskThreadsLoading(true);
    try {
      const res = await ApiClass.getNodeRequest(
        `admin/ask-ai/threads/${encodeURIComponent(chatId)}`
      );
      const thread = res?.data?.status_code == 1 ? res.data.data : null;
      if (!thread?.chatId) return;
      const slug = thread.agentSlug || agentSlug;
      setAgentSlug(slug);
      setAskChatId(thread.chatId);
      const restored = (thread.messages || []).map((item) => ({
        from: item.role === "assistant" ? "bot" : "user",
        text: item.content || "",
        messageId: item.role === "assistant" ? item.messageId : null,
        feedback: item.feedback || null,
        instant: true,
      }));
      setAskMessages(
        restored.length
          ? restored
          : [{ from: "bot", text: introForAgent(slug, false), instant: true }]
      );
      setAskHistoryOpen(false);
      askStickToBottom.current = true;
    } catch {
      setAskMessages((m) => [
        ...m,
        { from: "bot", text: "Could not open that thread.", instant: true },
      ]);
    } finally {
      setAskThreadsLoading(false);
      focusInput(askInputRef);
    }
  };

  const switchAgent = (slug) => {
    setAskHistoryOpen(false);
    setAgentSlug(slug);
    setAskChatId(null);
    setAskMessages([
      {
        from: "bot",
        text: introForAgent(slug, true),
        instant: true,
        notice: true,
      },
    ]);
  };

  const renderPanel = ({
    title,
    onClose,
    childrenHeader,
    listItems,
    listEndRef,
    listRef,
    stickToBottomRef,
    isLoading,
    formOnSubmit,
    formInputRef,
    formValue,
    formOnChange,
    placeholder,
    onBotChoice,
    onSaveStandards,
    onCancelStandards,
    onSaveQuestions,
    onCancelQuestions,
    onAddVisuals,
    onSkipVisuals,
    onFeedback,
    headerExtra,
    historyPanel,
  }) => {
    const minimized = panelMode === "min";
    const maximized = panelMode === "max";
    return (
    <div style={{ ...panel, ...(minimized ? panelMin : null), ...(maximized ? panelMax : null) }}>
      <div style={header}>
        <strong>{title}</strong>
        <div style={headerActions}>
          {headerExtra}
          <button
            type="button"
            style={iconBtn}
            title={minimized ? "Restore" : "Minimize"}
            aria-label={minimized ? "Restore" : "Minimize"}
            onClick={() => setPanelMode(minimized ? "default" : "min")}
          >
            {minimized ? "▢" : "–"}
          </button>
          <button
            type="button"
            style={iconBtn}
            title={maximized ? "Restore" : "Maximize"}
            aria-label={maximized ? "Restore" : "Maximize"}
            onClick={() => setPanelMode(maximized ? "default" : "max")}
          >
            {maximized ? "❐" : "□"}
          </button>
          <button type="button" style={iconBtn} title="Close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
      </div>
      {minimized ? null : (
        <>
      {childrenHeader}
      {historyPanel || (
      <div
        ref={listRef}
        data-chat-scroll=""
        style={list}
        onScroll={stickToBottomRef ? (event) => onListScroll(event, stickToBottomRef) : undefined}
      >
        {listItems.map((m, i) => (
          m.notice || (m.from === "bot" && isAgentNotice(m.text)) ? (
            <details key={i} className="ask-agent-note">
              <summary>{m.text.length > 72 ? `${m.text.slice(0, 72).trim()}…` : m.text}</summary>
              {m.text.length > 72 ? <p>{m.text}</p> : null}
            </details>
          ) : (
          <div key={i} style={m.from === "user" ? userBubble : botBubbleWrap}>
            <div style={m.from === "user" ? undefined : botBubbleInner}>
              {m.from === "bot" ? (
                <WritingReply
                  text={m.text}
                  instant={Boolean(m.instant) || i !== listItems.length - 1}
                  stickToBottomRef={stickToBottomRef}
                  onChoice={onBotChoice}
                  onSaveStandards={onSaveStandards ? (payload) => onSaveStandards(i, payload) : undefined}
                  onCancelStandards={onCancelStandards ? () => onCancelStandards(i) : undefined}
                  onSaveQuestions={onSaveQuestions ? (payload) => onSaveQuestions(i, payload) : undefined}
                  onCancelQuestions={onCancelQuestions ? () => onCancelQuestions(i) : undefined}
                  onAddVisuals={onAddVisuals ? (payload) => onAddVisuals(i, payload) : undefined}
                  onSkipVisuals={onSkipVisuals ? () => onSkipVisuals(i) : undefined}
                />
              ) : (
                m.text
              )}
            </div>
            {m.from === "bot" && m.messageId && onFeedback ? (
              m.feedbackNoted || m.feedback ? (
                <div style={feedbackBlock}>
                  <div style={feedbackPrompt}>
                    {m.feedback === "unlike"
                      ? "Thanks for the feedback — we’ll use it to improve."
                      : "Thanks for the feedback — glad this helped."}
                  </div>
                </div>
              ) : (
                <div style={feedbackBlock}>
                  <div style={feedbackPrompt}>Was this response helpful?</div>
                  <div style={feedbackRow}>
                    <button
                      type="button"
                      style={feedbackBtn}
                      title="Like"
                      aria-label="Like"
                      onClick={() => onFeedback(i, "like")}
                    >
                      👍
                    </button>
                    <button
                      type="button"
                      style={feedbackBtn}
                      title="Unlike"
                      aria-label="Unlike"
                      onClick={() => onFeedback(i, "unlike")}
                    >
                      👎
                    </button>
                  </div>
                </div>
              )
            ) : null}
          </div>
          )
        ))}
        {isLoading ? <GeneratingStatus /> : null}
        <div ref={listEndRef} />
      </div>
      )}
      {historyPanel ? null : (
      <form onSubmit={formOnSubmit} style={form}>
        <input
          ref={formInputRef}
          style={inputBox}
          value={formValue}
          onChange={formOnChange}
          placeholder={placeholder}
          autoFocus
        />
        <button
          type="submit"
          style={sendBtn}
          disabled={isLoading || !formValue.trim()}
          onMouseDown={(e) => e.preventDefault()}
        >
          Send
        </button>
      </form>
      )}
        </>
      )}
    </div>
    );
  };

  return (
    <>
      {askOpen || open ? (
        <div style={panelMode === "max" ? wrapFull : { ...wrap, bottom: 96 }}>
          {askOpen
            ? renderPanel({
                title: "Ask AI",
                onClose: () => {
                  setAskOpen(false);
                  setAskHistoryOpen(false);
                  setPanelMode("default");
                },
                headerExtra: (
                  <>
                    <button
                      type="button"
                      style={iconBtn}
                      title="Start a new chat"
                      aria-label="Start a new chat"
                      onClick={startNewAskThread}
                    >
                      <LuMessageSquarePlus size={18} />
                    </button>
                    <button
                      type="button"
                      style={iconBtn}
                      title={askHistoryOpen ? "Back to this thread" : "Thread history"}
                      aria-label={askHistoryOpen ? "Back to this thread" : "Thread history"}
                      onClick={() => (askHistoryOpen ? setAskHistoryOpen(false) : openAskHistory())}
                    >
                      {askHistoryOpen ? <LuArrowLeft size={18} /> : <LuHistory size={18} />}
                    </button>
                  </>
                ),
                historyPanel: askHistoryOpen ? (
                  <div ref={askHistoryRef} style={historyList} onScroll={onHistoryScroll}>
                    <button type="button" style={newThreadRow} onClick={startNewAskThread}>
                      <strong style={newThreadLabel}>
                        <LuMessageSquarePlus size={16} />
                        Start new chat
                      </strong>
                      <span style={threadMeta}>
                        {AGENTS.find((item) => item.slug === agentSlug)?.name || "This agent"}
                      </span>
                    </button>
                    {askThreadsLoading ? (
                      <div style={threadEmpty}>Loading threads...</div>
                    ) : askThreads.length ? (
                      askThreads.map((thread) => (
                        <button
                          key={thread.chatId}
                          type="button"
                          style={
                            thread.chatId === askChatId
                              ? { ...threadRow, ...threadRowActive }
                              : threadRow
                          }
                          onClick={() => openAskThread(thread.chatId)}
                        >
                          <strong style={threadTitle}>{thread.title || "Thread"}</strong>
                          <span style={threadMeta}>
                            {thread.messageCount || 0} messages
                            {thread.updatedAt
                              ? ` · ${new Date(thread.updatedAt).toLocaleString()}`
                              : ""}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div style={threadEmpty}>No earlier threads for this agent.</div>
                    )}
                    {askThreadsMore ? <div style={threadEmpty}>Loading more...</div> : null}
                  </div>
                ) : null,
                childrenHeader: (
                  <div style={agentRow}>
                    {AGENTS.map((agent) => (
                      <button
                        key={agent.slug}
                        type="button"
                        style={agentSlug === agent.slug ? agentChipActive : agentChip}
                        onClick={() => switchAgent(agent.slug)}
                      >
                        {agent.name.replace(" Agent", "")}
                      </button>
                    ))}
                  </div>
                ),
                listItems: askMessages,
                listEndRef: askEndRef,
                listRef: askListRef,
                stickToBottomRef: askStickToBottom,
                isLoading: askLoading,
                formOnSubmit: sendAskAi,
                formInputRef: askInputRef,
                formValue: askInput,
                formOnChange: (e) => setAskInput(e.target.value),
                placeholder: "Ask the selected agent...",
                onBotChoice: sendAskAiMessage,
                onSaveStandards: saveAskStandards,
                onCancelStandards: cancelAskStandards,
                onSaveQuestions: saveAskQuestions,
                onCancelQuestions: cancelAskQuestions,
                onAddVisuals: addAskVisuals,
                onSkipVisuals: skipAskVisuals,
                onFeedback: setAskFeedback,
              })
            : null}

          {open
            ? renderPanel({
                title: "Chat",
                onClose: () => {
                  setOpen(false);
                  setPanelMode("default");
                },
                listItems: messages,
                listEndRef: endRef,
                listRef: chatListRef,
                stickToBottomRef: chatStickToBottom,
                isLoading: loading,
                formOnSubmit: send,
                formInputRef: inputRef,
                formValue: input,
                formOnChange: (e) => setInput(e.target.value),
                placeholder: "Type a message...",
              })
            : null}
        </div>
      ) : null}

      <div style={{ ...wrap, bottom }}>
        <div style={fabStack}>
          {!open && !askOpen && showAskAi ? (
            <button
              type="button"
              style={fab}
              onClick={() => {
                setOpen(false);
                setAskOpen(true);
                setPanelMode("default");
              }}
            >
              Ask AI
            </button>
          ) : null}
          <button type="button" style={fab} onClick={openHelp}>
            <span style={helpIconWrap} aria-hidden="true">
              ?
            </span>
            Help
          </button>
        </div>
      </div>
    </>
  );
}

const wrap = {
  position: "fixed",
  right: 16,
  bottom: 16,
  zIndex: 1050,
};

const fabStack = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-end",
  gap: 10,
};

const fab = {
  border: 0,
  borderRadius: 999,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  height: 40,
  padding: "0 16px",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  boxShadow: "0 4px 8px rgba(0,0,0,.12), 0 0 2px rgba(0,0,0,.12)",
  cursor: "pointer",
};

const helpIconWrap = {
  width: 16,
  height: 16,
  border: "2px solid #fff",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
};

const panel = {
  width: 420,
  maxWidth: "calc(100vw - 24px)",
  height: 520,
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 8px 28px rgba(0,0,0,.2)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const header = {
  background: "rgb(142, 68, 173)",
  color: "#fff",
  padding: "10px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const headerActions = {
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const historyList = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: 12,
  background: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const threadRow = {
  border: "1px solid #e2e8f0",
  background: "#fff",
  borderRadius: 10,
  padding: "10px 12px",
  textAlign: "left",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const threadRowActive = {
  borderColor: "rgb(142, 68, 173)",
  background: "rgba(142, 68, 173, 0.08)",
};

const newThreadRow = {
  ...threadRow,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  border: 0,
};

const newThreadLabel = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const threadTitle = {
  fontSize: 14,
  fontWeight: 700,
};

const threadMeta = {
  fontSize: 12,
  opacity: 0.8,
};

const threadEmpty = {
  color: "#64748b",
  fontSize: 13,
  padding: "8px 4px",
};

const iconBtn = {
  border: 0,
  background: "transparent",
  color: "#fff",
  fontSize: 18,
  lineHeight: 1,
  cursor: "pointer",
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
};

const wrapFull = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
};

const panelMin = {
  height: "auto",
  width: 280,
};

const panelMax = {
  width: "100vw",
  height: "100vh",
  maxWidth: "100vw",
  borderRadius: 0,
  boxShadow: "none",
};

const agentRow = {
  display: "flex",
  gap: 6,
  padding: "8px 10px",
  background: "#f8fafc",
  flexWrap: "wrap",
};

const agentChip = {
  border: 0,
  background: "rgba(142, 68, 173, 0.16)",
  color: "#9ca3af",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

const agentChipActive = {
  ...agentChip,
  color: "#000",
};

const list = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: 12,
  background: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const botBubble = {
  alignSelf: "flex-start",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "8px 10px",
  maxWidth: "92%",
  fontSize: 14,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
};

const botBubbleWrap = {
  ...botBubble,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  paddingBottom: 6,
};

const botBubbleInner = {
  width: "100%",
};

const feedbackBlock = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 6,
  marginTop: 2,
};

const feedbackPrompt = {
  fontSize: 11,
  color: "#64748b",
  lineHeight: 1.3,
};

const feedbackRow = {
  display: "flex",
  gap: 4,
  alignItems: "center",
};

const feedbackBtn = {
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  borderRadius: 999,
  width: 26,
  height: 22,
  padding: 0,
  cursor: "pointer",
  fontSize: 12,
  lineHeight: 1,
};

const feedbackBtnActive = {
  ...feedbackBtn,
  background: "rgba(142, 68, 173, 0.12)",
  borderColor: "rgb(142, 68, 173)",
};

const userBubble = {
  ...botBubble,
  alignSelf: "flex-end",
  background: "rgb(142, 68, 173)",
  color: "#fff",
  border: 0,
  whiteSpace: "pre-wrap",
};

const form = {
  display: "flex",
  gap: 8,
  padding: 10,
  borderTop: "1px solid #e2e8f0",
};

const inputBox = {
  flex: 1,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 14,
};

const sendBtn = {
  border: 0,
  borderRadius: 8,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  fontWeight: 600,
  padding: "8px 12px",
  cursor: "pointer",
};
